import { entrancesOf } from '../building.js';
import { LANDING_GRACE_REACTION_MS, TICK_MS } from '../constants.js';
import type { GameState } from '../types.js';
import { chargeGaveUp } from './service.js';

/**
 * Move passengers from `unspawned` to `waiting` once their scheduled time
 * arrives — and turn them away if there is nowhere to stand.
 *
 * The landing rule is a building rule (v0.5): some buildings have corridors that
 * hold a queue and some do not. Where they do not, "I will come back for them"
 * stops being free, because the fourth person to reach a three-person landing
 * never joins the queue at all.
 *
 * It is charged as a loss, not as a discount, and it happens the instant they
 * arrive. That visibility is the point: a penalty the player cannot watch
 * happen is a percentage in disguise, and the roadmap names that as a way this
 * design fails.
 *
 * But never before the lift could have come (ADR-0050). Fifteen hotel shifts
 * turned someone away in their first seconds, with the car still at the lobby,
 * and the owner lost 120 three seconds into a shift (PLAYTEST_LOG, 2026-10-28).
 * Until a floor's grace is over, someone who finds it full waits to arrive: they
 * come out when a place frees, and are turned away only if it is still full
 * when the grace ends.
 */
export function applySpawns(state: GameState): void {
  const landing = state.building.landingCapacity;
  const entrances = entrancesOf(state.building);

  for (const passenger of state.passengers) {
    if (passenger.state !== 'unspawned' || passenger.spawnMs > state.elapsedMs) continue;

    if (
      landing !== undefined &&
      // Coming out of a room, not arriving from outside (ADR-0029).
      passenger.follows === null &&
      // Staff work here (ADR-0031): never turned away, they fill it instead.
      !passenger.staff &&
      !entrances.includes(passenger.origin) &&
      standingOn(state, passenger.origin) >= landing
    ) {
      if (state.elapsedMs < graceFor(state, passenger.origin)) continue;
      passenger.state = 'abandoned';
      passenger.abandonedMs = state.elapsedMs;
      passenger.lostReason = 'no-room';
      chargeGaveUp(state, passenger);
      continue;
    }

    passenger.state = 'waiting';
    // From their scheduled arrival, not from the tick that noticed it, which is
    // the same tick unless a full floor held them back: then from when they came
    // out, since they were not waiting on the landing before.
    const held = state.elapsedMs - passenger.spawnMs >= TICK_MS;
    passenger.waitingSinceMs = held ? state.elapsedMs : passenger.spawnMs;
  }
}

/**
 * How long into the shift a floor turns nobody away: a person's reaction, the
 * car's travel there from where it starts, its doors, and one person in. The
 * car the shift is played with, so a faster lift has a shorter grace.
 */
export function graceFor(state: GameState, floor: number): number {
  const { spec } = state;
  return (
    LANDING_GRACE_REACTION_MS +
    Math.abs(floor - spec.startFloor) * spec.msPerFloor +
    spec.doorOpenMs +
    spec.transferMs
  );
}

/** How many places are already taken on this floor: a cart takes two (ADR-0031). */
function standingOn(state: GameState, floor: number): number {
  let count = 0;
  for (const p of state.passengers) {
    if (p.state === 'waiting' && p.standingFloor === floor) count += p.landingSpace;
  }
  return count;
}
